/**
 * Markdown simples das respostas do Assistente, lido para uma árvore de
 * blocos (sem HTML): parágrafos com quebra de linha, títulos (#, ##, ###),
 * listas com "-", "*" ou "1." e um nível de aninhamento, **negrito**,
 * *itálico* e `código`. O componente `Markdown` transforma a árvore em
 * elementos React, então nenhum texto da IA vira tag.
 *
 * Marca que não fecha fica como texto: "5 * 3" continua "5 * 3".
 */

const TITULO = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const ITEM = /^(\s*)([-*+•]|\d{1,3}[.)])\s+(.*)$/;
const SEPARADOR = /^\s*([-*_])(\s*\1){2,}\s*$/;
const RECUO_DE_SUBITEM = 2;

function recuo(espacos) {
  return espacos.replace(/\t/g, "    ").length;
}

function ehLetraOuNumero(caractere) {
  return Boolean(caractere) && /[\p{L}\p{N}]/u.test(caractere);
}

/** Fecha `marca` simples (* ou _) a partir de `inicio`, pulando as duplas (**). */
function fimDaMarcaSimples(texto, marca, inicio) {
  for (let j = inicio; j < texto.length; j += 1) {
    if (texto[j] !== marca) continue;
    if (texto[j + 1] === marca) {
      j += 1;
      continue;
    }
    if (/\s/.test(texto[j - 1])) continue;
    if (marca === "_" && ehLetraOuNumero(texto[j + 1])) continue;
    return j;
  }
  return -1;
}

/** Trechos de uma linha: texto, negrito, itálico e código (negrito e itálico aninham). */
export function lerTrechos(texto) {
  const trechos = [];
  let solto = "";
  const soltar = () => {
    if (solto) trechos.push({ tipo: "texto", texto: solto });
    solto = "";
  };

  let i = 0;
  while (i < texto.length) {
    const caractere = texto[i];

    if (caractere === "`") {
      const fim = texto.indexOf("`", i + 1);
      if (fim > i + 1) {
        soltar();
        trechos.push({ tipo: "codigo", texto: texto.slice(i + 1, fim) });
        i = fim + 1;
        continue;
      }
    }

    const dupla = texto.slice(i, i + 2);
    if (dupla === "**" || dupla === "__") {
      let fim = texto.indexOf(dupla, i + 2);
      // "***" no fim fecha um itálico de dentro e depois o negrito: **a *b***.
      while (fim > 0 && texto[fim + 2] === caractere) fim += 1;
      if (fim > i + 2 && !/\s/.test(texto[i + 2]) && !/\s/.test(texto[fim - 1])) {
        soltar();
        trechos.push({ tipo: "negrito", filhos: lerTrechos(texto.slice(i + 2, fim)) });
        i = fim + 2;
        continue;
      }
    }

    const podeAbrirItalico =
      (caractere === "*" || (caractere === "_" && !ehLetraOuNumero(texto[i - 1]))) &&
      texto[i + 1] &&
      !/\s/.test(texto[i + 1]) &&
      texto[i + 1] !== caractere;
    if (podeAbrirItalico) {
      const fim = fimDaMarcaSimples(texto, caractere, i + 1);
      if (fim > i + 1) {
        soltar();
        trechos.push({ tipo: "italico", filhos: lerTrechos(texto.slice(i + 1, fim)) });
        i = fim + 1;
        continue;
      }
    }

    solto += caractere;
    i += 1;
  }
  soltar();
  return trechos;
}

function novaLista(ordenada, marcador) {
  return { tipo: "lista", ordenada, inicio: ordenada ? parseInt(marcador, 10) || 1 : 1, itens: [] };
}

/** Árvore de blocos a partir do texto. Texto vazio vira lista vazia. */
export function lerMarkdown(texto) {
  const blocos = [];
  let paragrafo = null;
  let lista = null;

  const fecharParagrafo = () => {
    if (paragrafo) blocos.push(paragrafo);
    paragrafo = null;
  };
  const fecharLista = () => {
    if (lista) blocos.push(lista);
    lista = null;
  };

  String(texto ?? "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .forEach((linhaCrua) => {
      const linha = linhaCrua.replace(/\s+$/, "");

      if (!linha.trim() || SEPARADOR.test(linha)) {
        fecharParagrafo();
        fecharLista();
        return;
      }

      const titulo = linha.match(TITULO);
      if (titulo) {
        fecharParagrafo();
        fecharLista();
        blocos.push({ tipo: "titulo", nivel: Math.min(titulo[1].length, 3), trechos: lerTrechos(titulo[2]) });
        return;
      }

      const item = linha.match(ITEM);
      if (item) {
        fecharParagrafo();
        const ordenada = /\d/.test(item[2]);
        const conteudo = { trechos: [lerTrechos(item[3])], sublista: null };
        const ultimo = lista?.itens[lista.itens.length - 1];
        if (recuo(item[1]) >= RECUO_DE_SUBITEM && ultimo) {
          ultimo.sublista = ultimo.sublista || novaLista(ordenada, item[2]);
          ultimo.sublista.itens.push({ trechos: conteudo.trechos, sublista: null });
          return;
        }
        if (!lista || lista.ordenada !== ordenada) {
          fecharLista();
          lista = novaLista(ordenada, item[2]);
        }
        lista.itens.push(conteudo);
        return;
      }

      // Linha recuada logo depois de um item continua o item (quebra de linha).
      if (lista && /^\s+/.test(linha)) {
        const ultimo = lista.itens[lista.itens.length - 1];
        const alvo = ultimo.sublista ? ultimo.sublista.itens[ultimo.sublista.itens.length - 1] : ultimo;
        alvo.trechos.push(lerTrechos(linha.trim()));
        return;
      }

      fecharLista();
      paragrafo = paragrafo || { tipo: "paragrafo", linhas: [] };
      paragrafo.linhas.push(lerTrechos(linha.trim()));
    });

  fecharParagrafo();
  fecharLista();
  return blocos;
}
